import { useState, useCallback, useEffect, type FormEvent } from 'react';
import { apiClient, ApiClientError } from '../services/api-client';
import { recordPutOutcome } from '../services/protective-put-service';
import {
  validatePutOutcomeInput,
  selectSchedule,
  computeCommission,
  computeExerciseFee,
  PutOutcome,
  InstrumentClass,
  type ValidationError,
} from '@covered-calls/shared';
import type {
  BrokerProfile,
  ProtectivePut,
  PutOutcomeInput,
  CommissionResult,
  Exchange,
} from '@covered-calls/shared';

/**
 * PutOutcomeDialog — record the end-of-life outcome of an active protective put.
 *
 * Supports the three PutOutcome results: expired worthless, sold to close, and
 * exercised. For a sell-to-close, captures the sale proceeds and closing
 * commission, with a commission/GST preview resolved from the position's
 * (exchange, OPTIONS) schedule. Submits a PutOutcomeInput to the protective-put
 * outcome endpoint.
 *
 * Validates: Requirements 7.1, 7.2, 7.3, 8.4
 */

export interface PutOutcomeDialogProps {
  positionId: string;
  /** The exchange the underlying trades on; keys the commission schedule selection. */
  exchange: Exchange;
  /** Share quantity — drives the close commission trade value. */
  quantity: number;
  /** The active protective put being closed out (for display and context). */
  protectivePut: ProtectivePut;
  /** Optional pre-loaded broker profiles; fetched on mount when omitted. */
  brokerProfiles?: BrokerProfile[];
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormState {
  outcome: PutOutcome;
  outcomeDate: string;
  saleProceedsPerShare: string;
  closeCommission: string;
  /** Exercise sale price per share (EXERCISED). Defaults to the put strike. */
  exerciseSalePrice: string;
  brokerProfileId: string;
}

const initialFormState: FormState = {
  outcome: PutOutcome.EXPIRED,
  outcomeDate: new Date().toISOString().split('T')[0],
  saleProceedsPerShare: '',
  closeCommission: '',
  exerciseSalePrice: '',
  brokerProfileId: '',
};

/** Human-readable labels for each outcome option. */
const OUTCOME_LABELS: Record<PutOutcome, string> = {
  [PutOutcome.EXPIRED]: 'Expired worthless',
  [PutOutcome.CLOSED]: 'Sold to close',
  [PutOutcome.EXERCISED]: 'Exercised',
};

export function PutOutcomeDialog({
  positionId,
  exchange,
  quantity,
  protectivePut,
  brokerProfiles: initialProfiles,
  onSuccess,
  onCancel,
}: PutOutcomeDialogProps) {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [brokerProfiles, setBrokerProfiles] = useState<BrokerProfile[]>(initialProfiles ?? []);
  // Commission/GST split from the shared calculator for the closing sale.
  const [commissionBreakdown, setCommissionBreakdown] = useState<CommissionResult | null>(null);
  // True when a profile is selected but has no schedule for this (exchange, OPTIONS) cell.
  const [noScheduleForLeg, setNoScheduleForLeg] = useState(false);

  const isClose = form.outcome === PutOutcome.CLOSED;
  const isExercise = form.outcome === PutOutcome.EXERCISED;

  // Fetch broker profiles on mount when not supplied by the parent.
  useEffect(() => {
    if (initialProfiles && initialProfiles.length > 0) {
      return;
    }
    async function loadProfiles() {
      try {
        const profiles = await apiClient.get<BrokerProfile[]>('/broker-profiles');
        setBrokerProfiles(profiles);
      } catch {
        // Non-critical: continue without profiles
      }
    }
    loadProfiles();
  }, [initialProfiles]);

  /**
   * Resolve the close commission via the shared calculator.
   *
   * Selects the schedule for this position's (exchange, OPTIONS) cell and
   * computes the commission/GST split using tradeValue = saleProceedsPerShare ×
   * quantity. Populates the commission field with the effective `total`
   * (commission + GST) and records the split for the preview. When the profile
   * has no schedule for this leg, falls back to manual entry.
   */
  const resolveCommission = useCallback(
    (profileId: string, proceedsStr: string) => {
      if (!profileId) {
        setCommissionBreakdown(null);
        setNoScheduleForLeg(false);
        return;
      }

      const profile = brokerProfiles.find((p) => p.profileId === profileId);
      if (!profile) {
        setCommissionBreakdown(null);
        setNoScheduleForLeg(false);
        return;
      }

      const schedule = selectSchedule(profile, exchange, InstrumentClass.OPTIONS);
      if (!schedule) {
        // No schedule for this leg — fall back to manual entry (do not crash).
        setCommissionBreakdown(null);
        setNoScheduleForLeg(true);
        return;
      }

      const proceeds = parseFloat(proceedsStr) || 0;
      const tradeValue = proceeds * quantity;
      const breakdown = computeCommission(schedule, { tradeValue, quantity });

      setNoScheduleForLeg(false);
      setCommissionBreakdown(breakdown);
      setForm((prev) => ({ ...prev, closeCommission: breakdown.total.toFixed(2) }));
    },
    [brokerProfiles, exchange, quantity]
  );

  const handleFieldChange = useCallback(
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => prev.filter((err) => err.field !== field));
      setServerError(null);
      // Sale proceeds drive the close trade value; recompute when it changes
      // and a broker profile is already selected.
      if (field === 'saleProceedsPerShare' && form.brokerProfileId) {
        resolveCommission(form.brokerProfileId, value);
      }
    },
    [resolveCommission, form.brokerProfileId]
  );

  // Changing the outcome away from CLOSED clears the close-specific preview.
  const handleOutcomeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const outcome = e.target.value as PutOutcome;
      setForm((prev) => ({ ...prev, outcome }));
      setErrors([]);
      setServerError(null);
      if (outcome !== PutOutcome.CLOSED) {
        setCommissionBreakdown(null);
        setNoScheduleForLeg(false);
      }
    },
    []
  );

  // Auto-populate commission when broker profile is selected.
  const handleBrokerProfileChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const profileId = e.target.value;
      setForm((prev) => ({ ...prev, brokerProfileId: profileId }));
      resolveCommission(profileId, form.saleProceedsPerShare);
      setServerError(null);
    },
    [resolveCommission, form.saleProceedsPerShare]
  );

  /**
   * Resolve the option exercise/assignment fee (contracts × per-contract rate +
   * GST) from the selected profile's (exchange, OPTIONS) schedule and populate
   * the fee field, so the exercise flow mirrors the auto-resolve the backend
   * performs. When the profile defines no exercise fee, leaves the field blank
   * for manual entry.
   */
  const resolveExerciseFee = useCallback(
    (profileId: string) => {
      if (!profileId) {
        setCommissionBreakdown(null);
        setNoScheduleForLeg(false);
        return;
      }
      const profile = brokerProfiles.find((p) => p.profileId === profileId);
      if (!profile) {
        setCommissionBreakdown(null);
        setNoScheduleForLeg(false);
        return;
      }
      const schedule = selectSchedule(profile, exchange, InstrumentClass.OPTIONS);
      if (!schedule) {
        setCommissionBreakdown(null);
        setNoScheduleForLeg(true);
        return;
      }
      const fee = computeExerciseFee(schedule, protectivePut.contracts);
      setNoScheduleForLeg(false);
      setCommissionBreakdown(fee);
      // Only auto-fill when the schedule actually defines an exercise fee;
      // otherwise leave the field for manual entry.
      if (fee.total > 0) {
        setForm((prev) => ({ ...prev, closeCommission: fee.total.toFixed(2) }));
      }
    },
    [brokerProfiles, exchange, protectivePut.contracts]
  );

  // Broker-profile change on the EXERCISED path: resolve the exercise fee.
  const handleExerciseProfileChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const profileId = e.target.value;
      setForm((prev) => ({ ...prev, brokerProfileId: profileId }));
      resolveExerciseFee(profileId);
      setServerError(null);
    },
    [resolveExerciseFee]
  );

  const getFieldError = (field: string): string | undefined => {
    return errors.find((err) => err.field === field)?.message;
  };

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setServerError(null);

      const input: PutOutcomeInput = {
        outcome: form.outcome,
        outcomeDate: form.outcomeDate,
        brokerProfileId: form.brokerProfileId || undefined,
      };

      if (form.outcome === PutOutcome.CLOSED) {
        input.saleProceedsPerShare = parseFloat(form.saleProceedsPerShare);
        input.closeCommission = parseFloat(form.closeCommission);
      }

      if (form.outcome === PutOutcome.EXERCISED) {
        // Exercise sells the shares at the exercise price (defaults to the put
        // strike when left blank) and closes the position. An optional stock-sale
        // commission is carried on closeCommission.
        if (form.exerciseSalePrice.trim() !== '') {
          input.exerciseSalePrice = parseFloat(form.exerciseSalePrice);
        }
        if (form.closeCommission.trim() !== '') {
          input.closeCommission = parseFloat(form.closeCommission);
        }
      }

      // Client-side validation
      const validationErrors = validatePutOutcomeInput(input);

      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors([]);
      setIsSubmitting(true);

      try {
        await recordPutOutcome(positionId, input);
        onSuccess();
      } catch (error) {
        if (error instanceof ApiClientError) {
          if (error.errors && error.errors.length > 0) {
            setErrors(error.errors.map((err) => ({ field: err.field, message: err.message })));
          } else {
            setServerError(error.message || 'An error occurred. Please try again.');
          }
        } else {
          setServerError('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, positionId, onSuccess]
  );

  return (
    <div
      role="dialog"
      aria-labelledby="put-outcome-dialog-title"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '8px',
          padding: '2rem',
          maxWidth: '560px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
        }}
      >
        <h2 id="put-outcome-dialog-title" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
          Record Protective Put Outcome
        </h2>
        <p style={{ color: 'var(--color-muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
          Long put — strike ${protectivePut.strikePrice.toFixed(2)}, premium (paid) $
          {protectivePut.premium.toFixed(2)}/share, {protectivePut.contracts} contract
          {protectivePut.contracts === 1 ? '' : 's'}, expiry {protectivePut.expiryDate}.
        </p>

        {serverError && (
          <div
            role="alert"
            style={{
              backgroundColor: 'var(--color-chip-danger-bg)',
              color: 'var(--color-chip-danger-text)',
              padding: '0.75rem 1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Outcome Selector */}
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="put-outcome"
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Outcome
              <PutOutcomeInfoButton />
            </label>
            <select
              id="put-outcome"
              value={form.outcome}
              onChange={handleOutcomeChange}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--color-input-border)',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
                backgroundColor: 'var(--color-surface)',
              }}
            >
              {(Object.values(PutOutcome) as PutOutcome[]).map((outcome) => (
                <option key={outcome} value={outcome}>
                  {OUTCOME_LABELS[outcome]}
                </option>
              ))}
            </select>
          </div>

          {/* Outcome Date */}
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="put-outcome-date"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Outcome Date
            </label>
            <input
              id="put-outcome-date"
              type="date"
              value={form.outcomeDate}
              onChange={handleFieldChange('outcomeDate')}
              aria-invalid={!!getFieldError('outcomeDate')}
              aria-describedby={getFieldError('outcomeDate') ? 'put-outcome-date-error' : undefined}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('outcomeDate') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('outcomeDate') && (
              <span id="put-outcome-date-error" style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>
                {getFieldError('outcomeDate')}
              </span>
            )}
          </div>

          {/* Sell-to-close fields — only shown for the CLOSED outcome */}
          {isClose && (
            <>
              {/* Sale Proceeds Per Share */}
              <div style={{ marginBottom: '1rem' }}>
                <label
                  htmlFor="put-sale-proceeds"
                  style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
                >
                  Sale Proceeds ($ per share)
                </label>
                <input
                  id="put-sale-proceeds"
                  type="number"
                  step="0.01"
                  min="0.00"
                  max="999999.99"
                  value={form.saleProceedsPerShare}
                  onChange={handleFieldChange('saleProceedsPerShare')}
                  aria-invalid={!!getFieldError('saleProceedsPerShare')}
                  aria-describedby={
                    getFieldError('saleProceedsPerShare') ? 'put-sale-proceeds-error' : undefined
                  }
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: `1px solid ${getFieldError('saleProceedsPerShare') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                  }}
                />
                {getFieldError('saleProceedsPerShare') && (
                  <span id="put-sale-proceeds-error" style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>
                    {getFieldError('saleProceedsPerShare')}
                  </span>
                )}
              </div>

              {/* Broker Profile Selector */}
              <div style={{ marginBottom: '1rem' }}>
                <label
                  htmlFor="put-broker-profile"
                  style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
                >
                  Broker Profile (optional)
                </label>
                <select
                  id="put-broker-profile"
                  value={form.brokerProfileId}
                  onChange={handleBrokerProfileChange}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--color-input-border)',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                    backgroundColor: 'var(--color-surface)',
                  }}
                >
                  <option value="">-- Select broker profile --</option>
                  {brokerProfiles.map((profile) => (
                    <option key={profile.profileId} value={profile.profileId}>
                      {profile.brokerName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Close Commission */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  htmlFor="put-close-commission"
                  style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
                >
                  Close Commission ($)
                </label>
                <input
                  id="put-close-commission"
                  type="number"
                  step="0.01"
                  min="0.00"
                  max="99999.99"
                  value={form.closeCommission}
                  onChange={handleFieldChange('closeCommission')}
                  aria-invalid={!!getFieldError('closeCommission')}
                  aria-describedby={
                    getFieldError('closeCommission') ? 'put-close-commission-error' : undefined
                  }
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: `1px solid ${getFieldError('closeCommission') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                  }}
                />
                {getFieldError('closeCommission') && (
                  <span id="put-close-commission-error" style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>
                    {getFieldError('closeCommission')}
                  </span>
                )}

                {/* Commission / GST split from the shared calculator */}
                {commissionBreakdown && (
                  <div
                    data-testid="put-close-commission-breakdown"
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      backgroundColor: 'var(--color-surface-2)',
                      borderRadius: '4px',
                      fontSize: '0.8125rem',
                      color: 'var(--color-muted)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Commission</span>
                      <span>${commissionBreakdown.commission.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>GST</span>
                      <span>${commissionBreakdown.gst.toFixed(2)}</span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontWeight: 600,
                        borderTop: '1px solid var(--color-border)',
                        marginTop: '0.25rem',
                        paddingTop: '0.25rem',
                      }}
                    >
                      <span>Total</span>
                      <span>${commissionBreakdown.total.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* No schedule for this (exchange, OPTIONS) cell — manual entry */}
                {noScheduleForLeg && (
                  <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-chip-warning-text)', marginTop: '0.25rem' }}>
                    This broker profile has no schedule for {exchange} options. Enter the commission manually.
                  </span>
                )}

                <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
                  Auto-populated from broker profile. Override if actual commission differs.
                </span>
              </div>
            </>
          )}

          {/* Exercise fields — shown for the EXERCISED outcome. Exercising the
              put sells the shares at the strike and closes the whole position. */}
          {isExercise && (
            <>
              <div
                role="status"
                style={{
                  backgroundColor: 'var(--color-brand-fill)',
                  color: 'var(--color-link)',
                  padding: '0.75rem 1rem',
                  borderRadius: '4px',
                  marginBottom: '1rem',
                  fontSize: '0.8125rem',
                }}
              >
                Exercising the put sells your {quantity.toLocaleString()} shares at the
                strike price and closes this position. The underlying sale carries no
                commission; only the broker's option exercise fee (if any) applies.
              </div>

              {/* Exercise Sale Price (defaults to strike) */}
              <div style={{ marginBottom: '1rem' }}>
                <label
                  htmlFor="put-exercise-price"
                  style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
                >
                  Exercise Sale Price ($ per share)
                </label>
                <input
                  id="put-exercise-price"
                  type="number"
                  step="0.01"
                  min="0.00"
                  max="999999.99"
                  value={form.exerciseSalePrice}
                  onChange={handleFieldChange('exerciseSalePrice')}
                  placeholder={`Defaults to strike $${protectivePut.strikePrice.toFixed(2)}`}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--color-input-border)',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                  }}
                />
                <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
                  Leave blank to sell at the put strike (${protectivePut.strikePrice.toFixed(2)}).
                </span>
              </div>

              {/* Broker Profile — selecting one auto-resolves the option
                  exercise fee (per-contract rate + GST) from its OPTIONS schedule. */}
              <div style={{ marginBottom: '1rem' }}>
                <label
                  htmlFor="put-exercise-broker-profile"
                  style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
                >
                  Broker Profile (optional)
                </label>
                <select
                  id="put-exercise-broker-profile"
                  value={form.brokerProfileId}
                  onChange={handleExerciseProfileChange}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--color-input-border)',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                    backgroundColor: 'var(--color-surface)',
                  }}
                >
                  <option value="">-- Select broker profile --</option>
                  {brokerProfiles.map((profile) => (
                    <option key={profile.profileId} value={profile.profileId}>
                      {profile.brokerName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Option Exercise Fee — auto-resolved from the selected profile's
                  OPTIONS schedule (contracts × per-contract rate + GST) when a
                  profile is chosen; otherwise entered manually. The underlying
                  share sale itself is commission-free. */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  htmlFor="put-exercise-commission"
                  style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
                >
                  Option Exercise Fee ($)
                </label>
                <input
                  id="put-exercise-commission"
                  type="number"
                  step="0.01"
                  min="0.00"
                  max="99999.99"
                  value={form.closeCommission}
                  onChange={handleFieldChange('closeCommission')}
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--color-input-border)',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                  }}
                />
                {/* Exercise fee commission/GST split from the shared calculator */}
                {isExercise && commissionBreakdown && commissionBreakdown.total > 0 && (
                  <div
                    data-testid="put-exercise-fee-breakdown"
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      backgroundColor: 'var(--color-surface-2)',
                      borderRadius: '4px',
                      fontSize: '0.8125rem',
                      color: 'var(--color-muted)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Exercise fee ({protectivePut.contracts} contract{protectivePut.contracts === 1 ? '' : 's'})</span>
                      <span>${commissionBreakdown.commission.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>GST</span>
                      <span>${commissionBreakdown.gst.toFixed(2)}</span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontWeight: 600,
                        borderTop: '1px solid var(--color-border)',
                        marginTop: '0.25rem',
                        paddingTop: '0.25rem',
                      }}
                    >
                      <span>Total</span>
                      <span>${commissionBreakdown.total.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* No OPTIONS schedule for this exchange — manual entry */}
                {isExercise && noScheduleForLeg && (
                  <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-chip-warning-text)', marginTop: '0.25rem' }}>
                    This broker profile has no schedule for {exchange} options. Enter the exercise fee manually.
                  </span>
                )}

                <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
                  The broker's fee on the option exercise (incl. any GST). Auto-resolved from the selected broker profile; override if your trade report differs. The share sale is commission-free.
                </span>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-muted)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                minHeight: '44px',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-brand)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                minHeight: '44px',
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              {isSubmitting ? 'Recording...' : 'Record Outcome'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


/**
 * Info button with a popup explaining how to choose the correct protective-put
 * outcome. Mirrors the IvInfoButton pattern used in PositionDetail: an "i"
 * toggle that reveals a tooltip, with a full-screen click-catcher to dismiss.
 */
function PutOutcomeInfoButton() {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setShowInfo((v) => !v)}
        aria-label="How do I choose the right outcome?"
        aria-expanded={showInfo}
        style={{
          background: 'none',
          border: '1px solid var(--color-muted)',
          borderRadius: '50%',
          width: '18px',
          height: '18px',
          minHeight: '18px',
          fontSize: '0.65rem',
          fontWeight: 700,
          color: 'var(--color-muted)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        i
      </button>
      {showInfo && (
        <>
          <div
            onClick={() => setShowInfo(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 1099 }}
          />
          <div
            role="tooltip"
            style={{
              position: 'absolute',
              top: '24px',
              left: '0',
              backgroundColor: 'var(--color-header-bg)',
              color: 'var(--color-header-text)',
              borderRadius: '8px',
              padding: '1rem',
              fontSize: '0.8rem',
              lineHeight: '1.5',
              width: '320px',
              zIndex: 1100,
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
              fontWeight: 400,
            }}
          >
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem', fontSize: '0.85rem' }}>Which outcome do I pick?</p>
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 600 }}>Expired worthless</span>
              <span> — you held the put to expiry and it lapsed with no value. Typical when the stock finished above the strike, so the protection was not needed. No proceeds; the premium you paid is the full cost.</span>
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 600 }}>Sold to close</span>
              <span> — you sold the put back to the market before expiry for some residual value. Enter the sale proceeds per share; the premium paid is offset by what you recovered.</span>
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 600 }}>Exercised</span>
              <span> — you used the put to sell your shares at the strike price (you "put" them to the counterparty). This sells the shares and closes the whole position. Choose this only if the stock was sold at the strike, not on the open market above it.</span>
            </div>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.72rem', color: 'var(--color-header-muted)' }}>
              Tip: on your broker report, a stock sale via a LMT (limit) order above the strike is a voluntary sale — the put most likely expired worthless. An exercise sells at the strike and is flagged with an exercise/assignment code.
            </p>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.72rem', color: 'var(--color-header-muted)' }}>
              Tap anywhere to close
            </p>
          </div>
        </>
      )}
    </span>
  );
}

